import React from 'react';
import { useRecoilValue } from 'recoil';
import { PageEnquiryForm } from '../../store';
import styles from './index.module.scss';

/**
 * The collection statement that sits under the enquiry form.
 *
 * Shown only when the page actually renders a form, because that is the only time the page collects anything:
 * a statement about handling personal information on a page that asks for none would be noise, and on an
 * offer whose panel-less band is the whole foot of the page it would read as the offer's own small print.
 *
 * The wording is fixed and legal - it is Subaru Australia's collection notice, not editorial copy - so it
 * lives here rather than being authored per offer, where it could be edited or left out. The two links are
 * the site's own privacy policy route and the published contact address.
 */
const PRIVACY_POLICY_URL = '/privacy-policy';
const PRIVACY_OFFICER_EMAIL = 'privacy.officer@subaru.com.au';

export const PrivacyStatement: React.FC = () => {
  const form = useRecoilValue(PageEnquiryForm);

  if (!form) return null;

  return (
    <section className={styles.statement} data-testid="offer-privacy-statement">
      <h2 className={styles.heading}>Privacy Collection Statement</h2>

      <p className={styles.body}>
        Subaru (Aust) Pty Ltd trading as (&lsquo;Subaru Australia&rsquo;)
        respects your privacy and is bound by the Australian Privacy Principles
        set out in the Privacy Act 1988 (Cth). Subaru Australia collects
        personal information to provide the product or service which you have
        requested. If you do not provide us with your personal information, we
        may not be able to respond to your enquiry. If you have consented, or if
        otherwise permitted by law, Subaru Australia will send you marketing
        communications, which may be of interest to you, including updates and
        special offers. Subaru Australia may disclose your personal information
        to Subaru Retailers, related companies or third parties that provide us
        with services, including to overseas locations (such as Japan, UK &amp;
        USA). You may contact Subaru Australia to seek access, update or correct
        your personal information. If you have any concerns or queries regarding
        our handling of your personal information, please let us know using the
        contact details set out below. The Privacy Officer shall review,
        investigate and respond to any privacy compliant within 10 working days
        (or advise if otherwise) of receipt of such complaint. For more
        information, read our{' '}
        <a href={PRIVACY_POLICY_URL} className={styles.link}>
          Privacy Policy
        </a>{' '}
        or write to the Privacy Officer, Subaru Australia, Level 2, 4 Burbank
        Place, Norwest NSW 2153 or send an email to{' '}
        <a href={`mailto:${PRIVACY_OFFICER_EMAIL}`} className={styles.link}>
          {PRIVACY_OFFICER_EMAIL}
        </a>
        .
      </p>
    </section>
  );
};
